import axios from 'axios'
import store from '@/store'
import { message } from 'ant-design-vue'

const service = axios.create({
  baseURL: '/api',
  timeout: 30000
})

let handlingUnauthorized = false

export function resetAuthRedirect() {
  handlingUnauthorized = false
}

function isAuthLoginRequest(config) {
  if (!config || !config.url) return false
  return String(config.url).indexOf('/auth/login') >= 0
}

function isUnauthorized(status, body) {
  if (status === 401) return true
  if (body && typeof body === 'object' && (body.code === 401 || body.code === '401')) return true
  return false
}

function handleUnauthorized() {
  if (handlingUnauthorized) return true
  handlingUnauthorized = true
  store.dispatch('logout')
  message.warning('登录已失效，请重新登录')
  import('@/router').then(({ default: router }) => {
    if (router.currentRoute.path !== '/login') {
      router.replace('/login')
    }
  })
  return true
}

function shouldTreatAsUnauthorized(status, body, error, config) {
  // 登录接口本身的业务失败（密码错误等）绝不当作会话失效
  if (isAuthLoginRequest(config)) return false
  if (handlingUnauthorized) return true
  if (isUnauthorized(status, body)) return true
  const msg = error && error.message
  if (msg && /401|未登录|Unauthorized/i.test(msg)) return true
  return false
}

function extractErrorMessage(body, error, fallback) {
  if (body && typeof body === 'object') {
    if (body.message) return body.message
    if (body.msg) return body.msg
  }
  if (error && error.message && error.message !== 'Network Error') return error.message
  return fallback || '请求失败'
}

service.interceptors.request.use(
  (config) => {
    const token = store.state.token
    if (token) {
      config.headers.Authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

service.interceptors.response.use(
  (response) => {
    const res = response.data
    if (res && typeof res === 'object' && 'code' in res && res.code !== 0 && res.code !== 200) {
      const config = response.config
      if (shouldTreatAsUnauthorized(response.status, res, null, config)) {
        handleUnauthorized()
        return Promise.reject(new Error(res.message || '未登录'))
      }
      // 登录页上残留的 handlingUnauthorized 不应吞掉业务错误提示
      if (handlingUnauthorized && !isAuthLoginRequest(config)) {
        return Promise.reject(new Error(res.message || '未登录'))
      }
      message.error(res.message || '请求失败')
      return Promise.reject(new Error(res.message || '请求失败'))
    }
    return res
  },
  (error) => {
    const status = error.response && error.response.status
    const body = error.response && error.response.data
    const config = error.config
    if (shouldTreatAsUnauthorized(status, body, error, config)) {
      handleUnauthorized()
      return Promise.reject(error)
    }
    if (handlingUnauthorized && !isAuthLoginRequest(config)) {
      return Promise.reject(error)
    }
    // 进入登录相关请求时清掉会话失效锁，避免密码错误无提示
    if (isAuthLoginRequest(config)) {
      handlingUnauthorized = false
    }
    const msg = extractErrorMessage(body, error, '网络错误')
    message.error(msg)
    return Promise.reject(new Error(msg))
  }
)

export default service
